<script setup lang="ts">
type DetailTab = {
  key: string
  label: string
  localeKey?: string
}

const props = withDefaults(
  defineProps<{
    tabs: DetailTab[]
    activeKey: string
    exchangeName?: string
    exchangeId?: string
  }>(),
  {
    exchangeName: '',
    exchangeId: '',
  },
)

const emit = defineEmits<{
  select: [key: string]
}>()

const { t } = useAppI18n()

const getTabLabel = (tab: DetailTab) => {
  if (!tab.localeKey) {
    return tab.label
  }

  const translatedLabel = t(tab.localeKey)

  return translatedLabel === tab.localeKey ? tab.label : translatedLabel
}

const matomo = useMatomo()

const tabsPlaceholderRef = ref<HTMLElement | null>(null)
const isTabsFixed = ref(false)
const tabsFixedStyle = ref<Record<string, string>>({})
const DETAIL_TABS_STICKY_TOP = 70
const DETAIL_TABS_HEIGHT = 60
let detailTabsScrollFrame: number | null = null

/** 在不支持 sticky 的运行环境中，将详情导航固定在顶部导航下方。 */
const updateDetailTabsPosition = () => {
  const placeholder = tabsPlaceholderRef.value

  if (!placeholder || typeof window === 'undefined' || window.innerWidth < 800) {
    isTabsFixed.value = false
    tabsFixedStyle.value = {}
    return
  }

  const placeholderRect = placeholder.getBoundingClientRect()
  const detailMain = placeholder.closest<HTMLElement>('.exchange-detail-main')
  const detailMainBottom = detailMain?.getBoundingClientRect().bottom ?? Number.POSITIVE_INFINITY
  const isRtl = document.documentElement.dir === 'rtl'
  const inlineStart = isRtl ? window.innerWidth - placeholderRect.right : placeholderRect.left
  const shouldFixTabs = placeholderRect.top <= DETAIL_TABS_STICKY_TOP && detailMainBottom > 0

  isTabsFixed.value = shouldFixTabs

  tabsFixedStyle.value = shouldFixTabs
    ? {
        top: `${Math.min(DETAIL_TABS_STICKY_TOP, detailMainBottom - DETAIL_TABS_HEIGHT)}px`,
        insetInlineStart: `${inlineStart}px`,
        width: `${placeholderRect.width}px`,
      }
    : {}
}

/** 合并连续滚动事件，避免重复读取导航与内容区的布局信息。 */
const handleDetailTabsScroll = () => {
  if (detailTabsScrollFrame !== null || typeof window === 'undefined') {
    return
  }

  detailTabsScrollFrame = window.requestAnimationFrame(() => {
    detailTabsScrollFrame = null
    updateDetailTabsPosition()
  })
}

onMounted(() => {
  updateDetailTabsPosition()
  window.addEventListener('scroll', handleDetailTabsScroll, { passive: true })
  window.addEventListener('resize', handleDetailTabsScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleDetailTabsScroll)
  window.removeEventListener('resize', handleDetailTabsScroll)

  if (detailTabsScrollFrame !== null) {
    window.cancelAnimationFrame(detailTabsScrollFrame)
    detailTabsScrollFrame = null
  }
})

const handleSelect = (tab: DetailTab) => {
  matomo.trackEvent('broker_column', {
    af_type: getTabLabel(tab),
    af_type1: tab.key,
    af_name: props.exchangeId || '',
    af_code: props.exchangeName || '',
  })
  emit('select', tab.key)
}
</script>

<template>
  <div ref="tabsPlaceholderRef" class="exchange-detail-tabs-sticky">
    <nav
      class="exchange-detail-tabs"
      :class="{ 'exchange-detail-tabs-fixed': isTabsFixed }"
      :style="tabsFixedStyle"
      aria-label="交易所详情导航"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="exchange-detail-tab"
        :class="{ 'exchange-detail-tab-active': activeKey === tab.key }"
        :aria-current="activeKey === tab.key ? 'true' : undefined"
        :data-exchange-detail-tab="tab.key"
        @click="handleSelect(tab)"
      >
        {{ getTabLabel(tab) }}
      </button>
    </nav>
  </div>
</template>

<style scoped lang="less">
.exchange-detail-tabs-sticky {
  width: 100%;
  height: 60px;
}

.exchange-detail-tabs {
  display: flex;
  width: 100%;
  height: 60px;
  align-items: stretch;
  gap: 24px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  background: #ffffff;
  scrollbar-width: none;
}

.exchange-detail-tabs-fixed {
  position: fixed;
  z-index: 15;
}

.exchange-detail-tabs::-webkit-scrollbar {
  display: none;
}

.exchange-detail-tab {
  position: relative;
  display: inline-flex;
  height: 60px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: var(--color-text-subtlest, #7d818a);
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  padding: 16px 0;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease;
}

.exchange-detail-tab:hover,
.exchange-detail-tab:focus-visible,
.exchange-detail-tab-active {
  color: var(--color-text, #292a2e);
}

.exchange-detail-tab:focus-visible {
  outline: 2px solid rgba(36, 104, 222, 0.3);
  outline-offset: -2px;
}

.exchange-detail-tab::after {
  position: absolute;
  bottom: 2%;
  left: 50%;
  width: 28px;
  height: 4px;
  content: '';
  background: var(--color-icon-default, #292a2e);
  border-radius: 999px;
  opacity: 0;
  transform: translateX(-50%) scaleX(0.5);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.exchange-detail-tab-active {
  font-weight: 600;
}

.exchange-detail-tab-active::after {
  opacity: 1;
  transform: translateX(-50%) scaleX(1);
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .exchange-detail-tabs {
    gap: 24px;
  }
}

@media screen and (max-width: 799px) {
  .exchange-detail-tabs {
    gap: 20px;
  }
}
</style>
